// pages/solutions/AgencyTeams.jsx
import { useNavigate } from 'react-router-dom';

export default function AgencyTeams() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "⚡",
      title: "Automated Workflows",
      description: "Eliminate repetitive tasks and speed up campaign delivery with intelligent automation."
    },
    {
      icon: "👥",
      title: "Client Collaboration",
      description: "Keep clients in the loop with real-time updates and approval workflows."
    },
    {
      icon: "📊",
      title: "Unified Dashboard",
      description: "Manage multiple client campaigns from a single, intuitive interface."
    },
    {
      icon: "🎨",
      title: "Creative Templates",
      description: "Launch campaigns faster with pre-built, customizable templates."
    },
    {
      icon: "📈",
      title: "Performance Reporting",
      description: "Generate client-ready reports automatically with real-time data."
    },
    {
      icon: "🔄",
      title: "Workflow Integration",
      description: "Connect with your existing tools and streamline your tech stack."
    }
  ];

  const benefits = [
    "Reduce campaign setup time by 60%",
    "Handle 3x more clients with the same team",
    "Improve client satisfaction scores",
    "Eliminate manual reporting tasks"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Agency Teams
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Streamline workflows and boost efficiency while making both your creative team and clients happy.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Start Free Trial
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Everything Your Agency Needs
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#00809D]/10 to-transparent">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Measurable Results
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6"
              >
                <div className="w-8 h-8 bg-[#00809D] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Agency?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of agencies already scaling with our platform.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Get Started Today
          </button>
        </div>
      </section>
    </div>
  );
}
